import { Modal, ModalOverlay, ModalContent, Box } from "@chakra-ui/react";
import { ReactNode, createContext, useContext } from "react";
import { customEvent, EventProps } from "../../utils/customEvent";
import { VStack, HStack, Button, baseColors } from "goi_common";
import { CloseButton } from "../CloseButton";

interface ModalContextValue {
  onClose: () => void;
  closeEvent?: EventProps;
  mainColor: string;
}

const ModalContext = createContext<ModalContextValue | null>(null);

const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("context가 존재하지 않습니다.");
  }
  return context;
};

interface GoiBaseModalButton {
  text: ReactNode;
  onClick: () => void;
  event?: EventProps;
}

/**
 * GoiBaseModal - 템플릿 모달 컴포넌트
 * 레이아웃이 고정된 모달 컴포넌트. header/footer를 props로 받아 일관된 구조를 강제함
 * 일반 모달 UI는 [정형] 패턴을 사용하고, 그 외의 경우 [커스텀] 패턴을 사용 (노션 모달 / 바텀시트 가이드 참고)
 *
 * @example
 * // 정형 패턴 (헤더/푸터 포함)
 * <GoiBaseModal
 *   isOpen={true}
 *   onClose={handleClose}
 *   mainColor={baseColors.primary_orange}
 *   header={<GoiBaseModal.Header title="제목" description="설명" />}
 *   footer={<GoiBaseModal.Footer primaryButton={{...}} />}
 * >
 *   <div>컨텐츠</div>
 * </GoiBaseModal>
 *
 * @example
 * // 커스텀 패턴 (헤더/푸터 없이)
 * <GoiBaseModal
 *   isOpen={true}
 *   onClose={handleClose}
 *   mainColor={baseColors.primary_orange}
 * >
 *   <div>커스텀 콘텐츠</div>
 * </GoiBaseModal>
 */

interface GoiBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  showOverlay?: boolean;
  showCloseButton?: boolean;
  closeEvent?: EventProps;
  closeOnOverlayClick?: boolean;
  mainColor?: string;
  minWidth?: string;
}

export const GoiBaseModal = ({
  isOpen,
  onClose,
  children,
  header,
  footer,
  showOverlay = true,
  showCloseButton = true,
  closeEvent,
  closeOnOverlayClick = true,
  mainColor = baseColors.primary_orange,
  minWidth,
}: GoiBaseModalProps) => {
  const handleClose = () => {
    if (closeEvent) customEvent(closeEvent);
    onClose();
  };

  const contextValue: ModalContextValue = {
    onClose,
    closeEvent,
    mainColor,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={closeOnOverlayClick}
        scrollBehavior="inside"
        isCentered={true}
        autoFocus={false}
        closeOnEsc={false}
        returnFocusOnClose={false}
      >
        {showOverlay && <ModalOverlay width="100%" height="100%" />}
        <ModalContent
          maxWidth={"360px"}
          minWidth={minWidth}
          margin={"20px"}
          borderRadius={"12px"}
        >
          {showCloseButton && <CloseButton onClose={handleClose} />}
          <VStack padding="20px" gap={16} width="100%" position="relative">
            {header}
            {children && <Box marginTop={header ? 0 : "40px"}>{children}</Box>}
            {footer}
          </VStack>
        </ModalContent>
      </Modal>
    </ModalContext.Provider>
  );
};

/**
 * GoiBaseModal.Footer - 모달 푸터 컴포넌트
 * 모달 하단에 버튼을 표시하는 컴포넌트. Context에서 mainColor를 자동으로 가져옵니다.
 *
 * @example
 * <GoiBaseModal.Footer
 *   primaryButton={{ text: "확인", onClick: handleConfirm }}
 *   secondaryButton={{ text: "취소", onClick: handleCancel }}
 *   buttonLayout="horizontal"
 * />
 */
const ModalFooter = ({
  primaryButton,
  secondaryButton,
  buttonLayout = "horizontal",
}: {
  primaryButton: GoiBaseModalButton;
  secondaryButton?: GoiBaseModalButton;
  buttonLayout?: "horizontal" | "vertical";
}) => {
  const { mainColor } = useModalContext();

  const handleButtonClick = (button: GoiBaseModalButton) => {
    if (button.event) customEvent(button.event);
    button.onClick();
  };

  // 단일 CTA 버튼
  if (!secondaryButton) {
    return (
      <Button
        onClick={() => handleButtonClick(primaryButton)}
        className="body_1_normal_m"
        css={{
          width: "100%",
          height: "48px",
          borderRadius: "6px",
          background: mainColor,
          color: "white",
          cursor: "pointer",
        }}
      >
        {primaryButton.text}
      </Button>
    );
  }

  // 2개 CTA 버튼 (horizontal / vertical)
  const Stack = buttonLayout === "horizontal" ? HStack : VStack;
  const isHorizontal = buttonLayout === "horizontal";

  const primaryButtonElement = (
    <Button
      onClick={() => handleButtonClick(primaryButton)}
      className="body_1_normal_m"
      css={{
        ...(isHorizontal && { flex: 1 }),
        height: "48px",
        borderRadius: "6px",
        background: mainColor,
        color: "white",
        cursor: "pointer",
      }}
    >
      {primaryButton.text}
    </Button>
  );

  const secondaryButtonElement = (
    <Button
      onClick={() => handleButtonClick(secondaryButton)}
      className="body_1_normal_m"
      css={{
        ...(isHorizontal && { flex: 1 }),
        height: "48px",
        borderRadius: "6px",
        border: `1px solid ${baseColors.line_accent}`,
        background: baseColors.white,
        color: baseColors.label_alternative_1,
        cursor: "pointer",
      }}
    >
      {secondaryButton.text}
    </Button>
  );

  return (
    <Stack width="100%" gap={8}>
      {isHorizontal ? (
        <>
          {secondaryButtonElement}
          {primaryButtonElement}
        </>
      ) : (
        <>
          {primaryButtonElement}
          {secondaryButtonElement}
        </>
      )}
    </Stack>
  );
};

/**
 * GoiBaseModal.Header - 모달 헤더 컴포넌트
 * 모달 상단에 제목과 설명을 표시하는 컴포넌트.
 *
 * @example
 * <GoiBaseModal.Header
 *   title="제목"
 *   description="설명"
 * />
 */
const ModalHeader = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => {
  return (
    <Box position="relative" width="100%">
      {(title || description) && (
        <VStack gap={8} width="100%">
          {title && (
            <Box className="subtitle_21_b" style={{ whiteSpace: "pre-line" }}>
              {title}
            </Box>
          )}
          {description && (
            <Box
              className="body_2_normal_r"
              style={{ whiteSpace: "pre-line" }}
              color={baseColors.label_alternative_1}
            >
              {description}
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
};

GoiBaseModal.Header = ModalHeader;
GoiBaseModal.Footer = ModalFooter;
